'use client'
import { useEffect, useRef } from 'react'
import DOMManager from '@/utils/DOMManager'

interface PaginationLinksProps {
  currentPage?: number
  totalPages?: number
  baseUrl?: string
}

export default function PaginationLinks({ currentPage = 1, totalPages = 1, baseUrl = '' }: PaginationLinksProps) {
  const cleanupRef = useRef<boolean>(false)

  useEffect(() => {
    const domManager = DOMManager.getInstance()
    cleanupRef.current = false

    // Clean up previous links first
    domManager.removeElementsByPrefix('pagination-link')

    // Only add pagination links if we have multiple pages and component hasn't been cleaned up
    if (totalPages > 1 && !cleanupRef.current) {
      // Canonical link for current page
      const canonicalUrl = currentPage === 1 ? baseUrl : `${baseUrl}/page/${currentPage}`
      const canonicalLink = document.createElement('link')
      canonicalLink.rel = 'canonical'
      canonicalLink.href = canonicalUrl
      domManager.addElement('pagination-link-canonical', canonicalLink)

      // Previous page link
      if (currentPage > 1) {
        const prevUrl = currentPage === 2 ? baseUrl : `${baseUrl}/page/${currentPage - 1}`
        const prevLink = document.createElement('link')
        prevLink.rel = 'prev'
        prevLink.href = prevUrl
        domManager.addElement('pagination-link-prev', prevLink)
      }

      // Next page link
      if (currentPage < totalPages) {
        const nextUrl = `${baseUrl}/page/${currentPage + 1}`
        const nextLink = document.createElement('link')
        nextLink.rel = 'next'
        nextLink.href = nextUrl
        domManager.addElement('pagination-link-next', nextLink)

        // Preload next page for better performance (but only if reasonable number of pages)
        if (totalPages <= 50) {
          const preloadLink = document.createElement('link')
          preloadLink.rel = 'preload'
          preloadLink.href = nextUrl
          preloadLink.setAttribute('as', 'document')
          domManager.addElement('pagination-link-preload', preloadLink)
        }
      }
    }

    // Cleanup function
    return () => {
      cleanupRef.current = true
      domManager.removeElementsByPrefix('pagination-link')
    }
  }, [currentPage, totalPages, baseUrl])

  return null // This component doesn't render anything visible
}
